import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Syringe, UserCog, Wrench } from "lucide-react";

import { ChartCard, Empty, RankingCard, Heatmap } from "./widgets";
import { COLORS, brl } from "./utils";

/**
 * One slide of the "Modo reunião" presentation overlay. Bigger fonts and
 * larger chart heights than the inline tab views, but otherwise renders
 * the same visualizations.
 */
export default function PresentationSlide({
  slide,
  data,
  monthSeries,
  pieShift,
  pieSusp,
  weekdaySeries,
  topSurgeons,
  topProcs,
  topAnesth,
  topHospitals,
  totals,
}) {
  const H = 420;

  if (slide === "geral") {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard title="Tendência mensal — casos" className="lg:col-span-2">
          {monthSeries.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={H}>
              <LineChart data={monthSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 13 }} />
                <YAxis tick={{ fontSize: 13 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 13 }} />
                <Line type="monotone" dataKey="total" stroke="#1d4ed8" strokeWidth={3} name="Casos" dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        <ChartCard title="Por turno">
          {pieShift.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={H}>
              <PieChart>
                <Pie data={pieShift} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={130} label={(e) => `${e.name}: ${e.value}`}>
                  {pieShift.map((entry, i) => <Cell key={`p-shift-${entry.name}`} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        <ChartCard title="Por dia da semana" className="lg:col-span-3">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={weekdaySeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="weekday" tick={{ fontSize: 13 }} />
              <YAxis tick={{ fontSize: 13 }} />
              <Tooltip />
              <Bar dataKey="total" fill="#1d4ed8" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    );
  }

  if (slide === "pessoas") {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RankingCard icon={UserCog} title="Top cirurgiões e auxiliares" rows={topSurgeons} subtitle="Soma cirurgião principal + auxiliar" />
        <RankingCard icon={Syringe} title="Top anestesistas" rows={topAnesth} />
        <RankingCard icon={Wrench} title="Top instrumentadores" rows={(data?.by_technician || []).slice(0, 10)} />
      </div>
    );
  }

  if (slide === "procedimentos") {
    return (
      <div className="space-y-6">
        <ChartCard title="Top procedimentos">
          {topProcs.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={Math.max(360, topProcs.length * 44)}>
              <BarChart data={topProcs} layout="vertical" margin={{ left: 150 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 13 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 13 }} width={180} />
                <Tooltip />
                <Bar dataKey="total" fill="#059669" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        <ChartCard title="Top hospitais">
          {topHospitals.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={Math.max(260, topHospitals.length * 44)}>
              <BarChart data={topHospitals} layout="vertical" margin={{ left: 150 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 13 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 13 }} width={180} />
                <Tooltip />
                <Bar dataKey="total" fill="#0891b2" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
    );
  }

  if (slide === "financeiro") {
    return (
      <div className="space-y-6">
        <ChartCard title="Evolução financeira mensal (Aprovado × Faturado × Recebido × Glosa)">
          {monthSeries.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={H}>
              <LineChart data={monthSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 13 }} />
                <YAxis tick={{ fontSize: 13 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <Tooltip formatter={(v) => brl(v)} />
                <Legend wrapperStyle={{ fontSize: 13 }} />
                <Line type="monotone" dataKey="approved" stroke="#7c3aed" strokeWidth={2.5} name="Aprovado" />
                <Line type="monotone" dataKey="billed" stroke="#1d4ed8" strokeWidth={2.5} name="Faturado" />
                <Line type="monotone" dataKey="received" stroke="#059669" strokeWidth={2.5} name="Recebido" />
                <Line type="monotone" dataKey="adjustments" stroke="#dc2626" strokeWidth={2} strokeDasharray="4 2" name="Glosa" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        <ChartCard title="Faturamento por cirurgião (top 10)">
          {topSurgeons.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={Math.max(360, topSurgeons.length * 48)}>
              <BarChart data={topSurgeons} layout="vertical" margin={{ left: 150 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 13 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 13 }} width={180} />
                <Tooltip formatter={(v) => brl(v)} />
                <Legend wrapperStyle={{ fontSize: 13 }} />
                <Bar dataKey="approved" fill="#7c3aed" name="Aprovado" />
                <Bar dataKey="billed" fill="#1d4ed8" name="Faturado" />
                <Bar dataKey="received" fill="#059669" name="Recebido" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
    );
  }

  if (slide === "heatmap") {
    return (
      <ChartCard title="Cirurgião × Procedimento — mapa de calor">
        <Heatmap hm={data?.heatmap} />
      </ChartCard>
    );
  }

  if (slide === "suspensoes") {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Suspensões por motivo">
          {pieSusp.length === 0 ? <Empty text="Sem suspensões no período." /> : (
            <ResponsiveContainer width="100%" height={H}>
              <PieChart>
                <Pie data={pieSusp} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={150} label={(e) => `${e.name}: ${e.value}`}>
                  {pieSusp.map((entry, i) => <Cell key={`p-susp-${entry.name}`} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        <ChartCard title="Resumo">
          <div className="space-y-3 pt-4">
            <div className="text-2xl font-display text-slate-900">
              <strong className="text-red-600 text-4xl">{totals.cases_suspended ?? 0}</strong>
              <span className="text-base text-slate-500 ml-2">suspensões no período</span>
            </div>
            <ul className="space-y-2 pt-4">
              {(data?.suspensions_by_reason || []).map((s) => (
                <li key={s.reason} className="flex items-center justify-between text-base border-b border-slate-100 pb-2">
                  <span className="text-slate-700">{s.label}</span>
                  <span className="font-bold text-slate-900 text-lg">{s.total}</span>
                </li>
              ))}
            </ul>
          </div>
        </ChartCard>
      </div>
    );
  }

  return <Empty />;
}
